import { IStateParamsService, IUrlRouterProvider } from 'angular-ui-router';
import kebabCase from 'lodash/fp/kebabCase';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/tagging', '/content-feeds/google');
  urlRouterProvider.when(
    '/content-feeds/google',
    '/content-feeds/google/published',
  );
  urlRouterProvider.when(
    '/content-feeds/google/pending',
    '/content-feeds/google/pending/my-tagged-media',
  );
  urlRouterProvider.when(
    '/tagging/pending',
    '/content-feeds/google/pending/my-tagged-media',
  );
  urlRouterProvider.when(
    '/tagging/pending/my-tagged-media',
    '/content-feeds/google/pending/my-tagged-media',
  );
  urlRouterProvider.when(
    '/tagging/pending/tagging-services',
    '/content-feeds/google/pending/tagging-services',
  );
  urlRouterProvider.when('/tagging/*path', '/content-feeds/google/{path}');
}

function TaggingStateFactory(state: IPartialState) {
  const data = {
    navGroup: NavGroup.COMMERCE,
    title: 'Google Product Collections',
    ...state.data,
    hasAccess: () => hasFeature('asset_feed_tagging'),
  };
  return StateFactory({
    ...state,
    data,
  });
}

const componentNameToPaginatedTemplate = (componentName: string) => {
  return `<${kebabCase(componentName)} page-number="::pageNumber" class="flex-tall cu-w-100" cu-page-scroll-to-top/>`;
};

const paginatedComponentController = [
  '$scope',
  '$stateParams',
  ($scope: Record<string, unknown>, $stateParams: IStateParamsService) => {
    // Page numbers are zero-indexed
    $scope.pageNumber = ($stateParams.page || 1) - 1;
  },
];

export const states = {
  tagging: TaggingStateFactory({
    url: '/content-feeds/google',
    template: componentNameToTemplate('taggingPage'),
    resolve: {
      ...lazyLoadModule(
        'curalate.tagging',
        () => import('@tagging/TaggingApp'),
      ),
    },
  }),
  'tagging.published': TaggingStateFactory({
    url: '/published?:page',
    views: {
      'tagging-main': {
        template: componentNameToPaginatedTemplate('taggingPublished'),
        controller: paginatedComponentController,
      },
    },
  }),
  'tagging.needs-review': TaggingStateFactory({
    url: '/needs-review?:page',
    views: {
      'tagging-main': {
        template: componentNameToPaginatedTemplate('taggingNeedsReview'),
        controller: paginatedComponentController,
      },
    },
  }),
  'tagging.needs-products': TaggingStateFactory({
    url: '/needs-products?:page',
    views: {
      'tagging-main': {
        template: componentNameToPaginatedTemplate('taggingNeedsProducts'),
        controller: paginatedComponentController,
      },
    },
  }),
  'tagging.pending': TaggingStateFactory({
    url: '/pending',
    views: {
      'tagging-main': {
        template:
          '<div ui-view="pending-main" class="cu-w-100 pure-bs3 flex-tall cu-h-100"></div>',
      },
    },
  }),
  'tagging.pending.my-tagged-media': TaggingStateFactory({
    url: '/my-tagged-media?:page',
    views: {
      'pending-main': {
        template: componentNameToPaginatedTemplate('taggingMyTaggedMedia'),
        controller: paginatedComponentController,
      },
    },
  }),
  'tagging.pending.tagging-services': TaggingStateFactory({
    url: '/tagging-services?:page',
    views: {
      'pending-main': {
        template: componentNameToPaginatedTemplate('taggingServices'),
        controller: paginatedComponentController,
      },
    },
    data: {
      hasAccess: () => hasFeature('tagging_services'),
    },
  }),
  'tagging.trash': TaggingStateFactory({
    url: '/trash?:page',
    views: {
      'tagging-main': {
        template: componentNameToPaginatedTemplate('taggingTrash'),
        controller: paginatedComponentController,
      },
    },
  }),
};
